/**
 * SOVRANOVA — Healing Session Player v2
 * Fixes:
 *   1. Sub-20Hz frequencies (7Hz theta, 4Hz delta) use BINAURAL BEATS
 *      (carrier 200Hz L + carrier+N Hz R = N Hz perceived beat)
 *   2. Audible frequencies (396Hz, 432Hz, 528Hz) play as direct sine tones
 *   3. Timer correct: durationMinutes * 60 * 1000
 */
import { useState, useEffect, useRef } from 'react'

const BREATH_PHASES = [
  { label: 'Breathe In...',  ms: 4000 },
  { label: 'Hold...',        ms: 2000 },
  { label: 'Breathe Out...', ms: 6000 },
  { label: 'Rest...',        ms: 2000 },
]

// ── Audio engine — binaural for sub-20Hz, direct tone for audible ─────────────
function createAudioEngine(hz) {
  const CARRIER = 200  // carrier frequency for binaural beats
  const isBinaural = hz < 20

  let ctx = null, oscL = null, oscR = null, gainL = null, gainR = null
  let osc = null, gain = null

  return {
    start() {
      if (ctx) return
      ctx = new (window.AudioContext || window.webkitAudioContext)()

      if (isBinaural) {
        // Binaural beat: left ear hears CARRIER, right ear hears CARRIER+hz
        // Brain perceives the difference as hz (e.g. 7 Hz theta)
        const merger = ctx.createChannelMerger(2)
        gainL = ctx.createGain(); gainR = ctx.createGain()
        oscL  = ctx.createOscillator(); oscR = ctx.createOscillator()

        oscL.type = 'sine'; oscL.frequency.setValueAtTime(CARRIER, ctx.currentTime)
        oscR.type = 'sine'; oscR.frequency.setValueAtTime(CARRIER + hz, ctx.currentTime)

        ;[gainL, gainR].forEach(g => {
          g.gain.setValueAtTime(0, ctx.currentTime)
          g.gain.linearRampToValueAtTime(0.15, ctx.currentTime + 3)
        })

        oscL.connect(gainL); gainL.connect(merger, 0, 0)
        oscR.connect(gainR); gainR.connect(merger, 0, 1)
        merger.connect(ctx.destination)
        oscL.start(); oscR.start()
      } else {
        // Direct audible tone
        osc  = ctx.createOscillator()
        gain = ctx.createGain()
        osc.type = 'sine'
        osc.frequency.setValueAtTime(hz, ctx.currentTime)
        gain.gain.setValueAtTime(0, ctx.currentTime)
        gain.gain.linearRampToValueAtTime(0.18, ctx.currentTime + 3)
        osc.connect(gain); gain.connect(ctx.destination)
        osc.start()
      }
    },

    stop() {
      if (!ctx) return
      const fadeGains = isBinaural ? [gainL, gainR] : [gain]
      fadeGains.forEach(g => {
        if (!g) return
        g.gain.cancelScheduledValues(ctx.currentTime)
        g.gain.setValueAtTime(g.gain.value, ctx.currentTime)
        g.gain.linearRampToValueAtTime(0, ctx.currentTime + 2)
      })
      setTimeout(() => {
        try {
          if (isBinaural) { oscL?.stop(); oscR?.stop() }
          else osc?.stop()
          ctx?.close()
        } catch {}
        ctx = oscL = oscR = gainL = gainR = osc = gain = null
      }, 2200)
    },

    pause() {
      ctx?.suspend()
    },

    resume() {
      ctx?.resume()
    },
  }
}

export function HealingSessionPlayer({ session, onClose, onComplete }) {
  const {
    name            = 'Healing Session',
    hz              = 396,
    durationMinutes = 18,
    type            = 'SOMATIC',
    emoji           = '💛',
  } = session

  const TOTAL_MS = durationMinutes * 60 * 1000
  const isBinaural = hz < 20
  const CARRIER = 200

  const [elapsed,   setElapsed]   = useState(0)
  const [paused,    setPaused]    = useState(false)
  const [done,      setDone]      = useState(false)
  const [phaseIdx,  setPhaseIdx]  = useState(0)
  const [audioErr,  setAudioErr]  = useState(false)

  const engineRef      = useRef(null)
  const timerRef       = useRef(null)
  const phaseTimerRef  = useRef(null)

  useEffect(() => {
    try {
      engineRef.current = createAudioEngine(hz)
      engineRef.current.start()
    } catch { setAudioErr(true) }

    timerRef.current = setInterval(() => {
      setElapsed(prev => {
        const next = prev + 500
        if (next >= TOTAL_MS) {
          clearInterval(timerRef.current)
          setDone(true)
          engineRef.current?.stop()
          return TOTAL_MS
        }
        return next
      })
    }, 500)

    return () => {
      clearInterval(timerRef.current)
      clearTimeout(phaseTimerRef.current)
      engineRef.current?.stop()
    }
  }, []) // eslint-disable-line

  useEffect(() => {
    if (paused || done) return
    phaseTimerRef.current = setTimeout(() => {
      setPhaseIdx(i => (i + 1) % BREATH_PHASES.length)
    }, BREATH_PHASES[phaseIdx].ms)
    return () => clearTimeout(phaseTimerRef.current)
  }, [phaseIdx, paused, done])

  const togglePause = () => {
    setPaused(p => {
      if (!p) {
        clearInterval(timerRef.current)
        clearTimeout(phaseTimerRef.current)
        engineRef.current?.pause()
      } else {
        engineRef.current?.resume()
        timerRef.current = setInterval(() => {
          setElapsed(prev => {
            const next = prev + 500
            if (next >= TOTAL_MS) {
              clearInterval(timerRef.current)
              setDone(true)
              engineRef.current?.stop()
              return TOTAL_MS
            }
            return next
          })
        }, 500)
      }
      return !p
    })
  }

  const pct     = Math.min(100, Math.round((elapsed / TOTAL_MS) * 100))
  const remMin  = Math.floor(Math.max(0, TOTAL_MS - elapsed) / 60000)
  const remSec  = Math.floor((Math.max(0, TOTAL_MS - elapsed) % 60000) / 1000)
  const breathLabel = done ? '✓ Done' : BREATH_PHASES[phaseIdx].label
  const circleScale = BREATH_PHASES[phaseIdx].label.includes('In') || BREATH_PHASES[phaseIdx].label.includes('Hold')
    ? 1.18 : 0.85

  // Describe what the user is actually hearing
  const audioDescription = isBinaural
    ? `Binaural beat: ${hz} Hz theta · Carrier: ${CARRIER} Hz`
    : `${hz} Hz tone`

  return (
    <div style={s.page}>
      <button style={s.back} onClick={() => { engineRef.current?.stop(); onClose?.() }}>← Back</button>

      <div style={s.header}>
        <div style={s.emoji}>{emoji}</div>
        <p style={s.tag}>{type} · {durationMinutes} MIN</p>
        <h1 style={s.title}>{name}</h1>
        <p style={s.freq}>{audioDescription}</p>
        {isBinaural && (
          <p style={s.binauralNote}>🎧 Use headphones for binaural beats to work</p>
        )}
      </div>

      <div style={s.circleWrap}>
        <div style={{ ...s.circleOuter, transform: `scale(${circleScale})` }}>
          <div style={s.circleInner}>
            <span style={s.breathLabel}>{breathLabel}</span>
          </div>
        </div>
      </div>

      <div style={s.progressSection}>
        <span style={s.progressLabel}>Progress</span>
        <span style={s.progressLabel}>{pct}%</span>
      </div>
      <div style={s.progressTrack}>
        <div style={{ ...s.progressFill, width: `${pct}%` }} />
      </div>

      <div style={s.freqBox}>
        <p style={s.freqTag}>ACTIVE TONE</p>
        <p style={s.freqHz}>{hz} Hz</p>
        {isBinaural && <p style={s.freqSub}>Perceived binaural beat · Carrier {CARRIER} Hz + {CARRIER + hz} Hz</p>}
        <div style={s.waveWrap}>
          {Array.from({ length: 18 }).map((_, i) => (
            <div key={i} style={{
              ...s.waveBar,
              animationDuration: `${0.4 + (i % 5) * 0.09}s`,
              animationPlayState: paused || done ? 'paused' : 'running',
              height: `${8 + Math.abs(Math.sin(i * 0.6)) * 22}px`,
            }} />
          ))}
        </div>
        {audioErr && <p style={s.audioErr}>Audio unavailable — allow audio in browser settings.</p>}
      </div>

      {done ? (
        <div style={s.completeWrap}>
          <p style={s.completeTitle}>✨ Session Complete</p>
          <p style={s.completeSub}>A few quiet minutes, just for you.</p>
          <p style={s.completeSub}>Take a moment to integrate what arose.</p>
          <button style={s.completeBtn} onClick={onComplete}>Complete Session ✓</button>
        </div>
      ) : (
        <button style={{ ...s.pauseBtn, ...(paused ? s.pauseBtnPaused : {}) }} onClick={togglePause}>
          {paused ? 'Resume ▶' : `Pause ⏸  ·  ${remMin}:${String(remSec).padStart(2, '0')} remaining`}
        </button>
      )}

      <style>{`
        @keyframes wavePulse {
          0%,100% { opacity:0.5; transform:scaleY(0.6) }
          50%      { opacity:1;   transform:scaleY(1.4) }
        }
      `}</style>
    </div>
  )
}

const s = {
  // Organic "deep room" restyle (Board P1, 2026-08-13). Values only.
  page:          { minHeight:'100vh', background:'linear-gradient(170deg,#2e2b25,#211e19)', display:'flex', flexDirection:'column', alignItems:'center', padding:'1.5rem 1.25rem 2.5rem', gap:'1.25rem' },
  back:          { alignSelf:'flex-start', background:'transparent', border:'none', color:'rgba(245,234,216,.5)', fontSize:'0.875rem', cursor:'pointer' },
  header:        { textAlign:'center', display:'flex', flexDirection:'column', alignItems:'center', gap:'0.35rem' },
  emoji:         { fontSize:'2.5rem' },
  tag:           { fontSize:'0.7rem', letterSpacing:'0.14em', color:'#c9a84c', textTransform:'uppercase', fontWeight:600 },
  title:         { fontFamily:'var(--og-fh, Georgia, serif)', fontSize:'1.75rem', fontWeight:400, color:'#f5ead8', letterSpacing:'-0.01em' },
  freq:          { fontSize:'0.8rem', color:'rgba(245,234,216,.45)' },
  binauralNote:  { fontSize:'0.75rem', color:'#c9a84c', background:'rgba(201,168,76,.1)', padding:'0.3rem 0.75rem', borderRadius:'1rem', border:'1px solid rgba(201,168,76,.25)' },
  circleWrap:    { display:'flex', alignItems:'center', justifyContent:'center', width:200, height:200 },
  circleOuter:   { width:160, height:160, borderRadius:'50%', border:'1.5px solid rgba(201,168,76,.3)', display:'flex', alignItems:'center', justifyContent:'center', transition:'transform 3s ease-in-out' },
  circleInner:   { width:120, height:120, borderRadius:'50%', background:'linear-gradient(135deg,rgba(201,168,76,.35),rgba(198,113,57,.3))', display:'flex', alignItems:'center', justifyContent:'center' },
  breathLabel:   { fontSize:'0.875rem', color:'#ffe9b8', fontWeight:500, textAlign:'center' },
  progressSection:{ width:'100%', maxWidth:520, display:'flex', justifyContent:'space-between' },
  progressLabel: { fontSize:'0.75rem', color:'rgba(245,234,216,.5)' },
  progressTrack: { width:'100%', maxWidth:520, height:4, background:'rgba(245,234,216,.1)', borderRadius:999, overflow:'hidden' },
  progressFill:  { height:'100%', background:'linear-gradient(90deg,#c9a84c,#ffc6a5)', borderRadius:999, transition:'width 0.5s linear' },
  freqBox:       { width:'100%', maxWidth:520, background:'rgba(245,234,216,.05)', border:'none', borderRadius:'20px', padding:'1rem 1.25rem', textAlign:'center', display:'flex', flexDirection:'column', alignItems:'center', gap:'0.4rem' },
  freqTag:       { fontSize:'0.62rem', letterSpacing:'0.16em', color:'#c9a84c', textTransform:'uppercase' },
  freqHz:        { fontSize:'1.5rem', fontWeight:700, color:'#f5f0e8' },
  freqSub:       { fontSize:'0.7rem', color:'rgba(255,255,255,.35)' },
  waveWrap:      { display:'flex', alignItems:'center', gap:3, height:40 },
  waveBar:       { width:3, borderRadius:2, background:'rgba(201,168,76,.6)', animation:'wavePulse 0.5s ease-in-out infinite alternate' },
  audioErr:      { fontSize:'0.72rem', color:'#e88', marginTop:'0.25rem' },
  pauseBtn:      { width:'100%', maxWidth:520, padding:'1rem', background:'#c67139', color:'#f5ead8', border:'none', borderRadius:'999px', fontSize:'1rem', fontWeight:600, cursor:'pointer' },
  pauseBtnPaused:{ background:'rgba(198,113,57,.15)', border:'1.5px solid rgba(198,113,57,.45)', color:'#ffc6a5' },
  completeWrap:  { width:'100%', maxWidth:520, display:'flex', flexDirection:'column', alignItems:'center', gap:'0.5rem' },
  completeTitle: { fontFamily:'var(--og-fh, Georgia, serif)', fontSize:'1.25rem', fontWeight:400, color:'#f5ead8' },
  completeSub:   { fontSize:'0.875rem', color:'rgba(245,234,216,.55)', textAlign:'center' },
  completeBtn:   { marginTop:'0.75rem', padding:'0.9rem 2rem', background:'#c67139', color:'#f5ead8', border:'none', borderRadius:'999px', fontSize:'1rem', fontWeight:600, cursor:'pointer' },
}
